/**
 * SettingsIbanOnPdfController — bloc « IBAN sur les PDF » de l'onglet
 * Encaissement de /parametres.html (EPIC-061 / 61.3).
 *
 * Hydrate depuis `GET /organization/settings` (`ibanOnPdf` :
 * `{ includeOnInvoices, includeOnQuotes, virementActive, virementHasIban }`).
 *
 * Comportement quand aucun provider Virement actif avec IBAN n'est
 * configuré (`virementActive === false` ou `virementHasIban === false`) :
 *  - les 2 toggles + le bouton « Modifier » sont désactivés ;
 *  - un hint contextuel pointe vers `/parametres-encaissement.html`.
 *
 * Persiste via `PUT /organization/settings/iban-on-pdf` au submit du
 * formulaire — payload `{ includeOnInvoices, includeOnQuotes }`.
 *
 * Convention CLAUDE.md respectée :
 *  - Pas d'HTML inline en JS : le résumé vit dans
 *    `<template id="tpl-iban-on-pdf-summary">`, cloné via
 *    `cloneTemplate` + `fillSlots`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class SettingsIbanOnPdfController extends BaseController {
  static targets = [
    "block",
    "summary",
    "form",
    "invoicesInput",
    "quotesInput",
    "submitBtn",
    "virementHint",
  ];

  async connect() {
    try {
      const data = await api.getSettings();
      this.hydrateFromApi(data?.ibanOnPdf);
    } catch (_error) {
      this.showError("Impossible de charger le bloc IBAN sur PDF.");
    }
  }

  hydrateFromApi(ibanOnPdf) {
    if (!ibanOnPdf || typeof ibanOnPdf !== "object") return;
    this._renderSummary(ibanOnPdf);
    this._populateForm(ibanOnPdf);
    this._applyConfigurableState(ibanOnPdf);
  }

  _renderSummary(state) {
    if (!this.hasSummaryTarget) return;
    const fragment = this.cloneTemplate("tpl-iban-on-pdf-summary");
    this.fillSlots(fragment, {
      invoicesLabel: state.includeOnInvoices
        ? "affiché sur les factures"
        : "non affiché sur les factures",
      quotesLabel: state.includeOnQuotes
        ? "affiché sur les devis"
        : "non affiché sur les devis",
    });
    while (this.summaryTarget.firstChild) {
      this.summaryTarget.removeChild(this.summaryTarget.firstChild);
    }
    this.summaryTarget.appendChild(fragment);
  }

  _populateForm(state) {
    if (this.hasInvoicesInputTarget) {
      this.invoicesInputTarget.checked = Boolean(state.includeOnInvoices);
    }
    if (this.hasQuotesInputTarget) {
      this.quotesInputTarget.checked = Boolean(state.includeOnQuotes);
    }
  }

  _applyConfigurableState(state) {
    const configurable = Boolean(state.virementActive && state.virementHasIban);
    if (this.hasInvoicesInputTarget) this.invoicesInputTarget.disabled = !configurable;
    if (this.hasQuotesInputTarget) this.quotesInputTarget.disabled = !configurable;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = !configurable;
    if (this.hasVirementHintTarget) this.virementHintTarget.hidden = configurable;

    // Bouton « Modifier » : désactivé si non configurable. On vise par
    // attribut plutôt que par target pour ne pas avoir à le porter
    // explicitement dans le HTML.
    if (this.hasBlockTarget) {
      const editBtn = this.blockTarget.querySelector("[data-edit-open]");
      if (editBtn) editBtn.disabled = !configurable;
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel — pattern aligné sur
  // SettingsPaymentProviderController.
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    if (!this.hasBlockTarget) return;
    this.blockTarget.dataset.editState = "editing";
    if (this.hasSummaryTarget) this.summaryTarget.hidden = true;
    if (this.hasFormTarget) {
      this.formTarget.hidden = false;
      const first = this.formTarget.querySelector(
        "input:not([type='hidden']), select, textarea",
      );
      if (first) first.focus();
    }
  }

  cancelBlock(event) {
    event.preventDefault();
    if (!this.hasBlockTarget) return;
    this.blockTarget.dataset.editState = "idle";
    if (this.hasFormTarget) this.formTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    event.preventDefault();
    if (!this.hasInvoicesInputTarget || !this.hasQuotesInputTarget) return;

    const payload = {
      includeOnInvoices: this.invoicesInputTarget.checked,
      includeOnQuotes: this.quotesInputTarget.checked,
    };

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
    try {
      const data = await api.updateIbanOnPdf(payload);
      this.hydrateFromApi(data?.ibanOnPdf);
      this.cancelBlock(event);
      this.showSuccess("Bloc IBAN sur PDF enregistré.");
    } catch (_error) {
      this.showError("Impossible d'enregistrer les réglages IBAN sur PDF.");
    } finally {
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
    }
  }
}
